import { Phone, MessageCircle, CheckCircle, Users, Shield, Clock, Award } from "lucide-react";

const AboutPage = () => {
  const phoneNumber = "05075537051";
  const whatsappNumber = "905075537051";
  const whatsappMessage = encodeURIComponent("Merhaba, eşya satmak istiyorum. Bilgi alabilir miyim?");
  const whatsappUrl = `https://wa.me/${whatsappNumber}?text=${whatsappMessage}`;

  const values = [
    {
      icon: Shield,
      title: "Güvenilirlik",
      description: "Şeffaf fiyatlandırma ve dürüst hizmet anlayışıyla çalışıyoruz.",
    },
    {
      icon: Clock,
      title: "Hızlı Hizmet",
      description: "7/24 ulaşılabilir olmamız ve aynı gün hizmet vermemiz ile öne çıkıyoruz.",
    },
    {
      icon: Users,
      title: "Müşteri Odaklılık",
      description: "Her müşterimize özel ilgi gösteriyor, ihtiyaçlarına uygun çözümler sunuyoruz.",
    },
    {
      icon: Award,
      title: "Profesyonellik",
      description: "Alanında uzman ekibimizle profesyonel değerlendirme ve hizmet sağlıyoruz.",
    },
  ];

  return (
    <div data-testid="about-page">
      {/* Hero Section */}
      <section className="bg-slate-50 py-16 md:py-24" data-testid="about-hero">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <p className="text-[#002FA7] font-semibold text-sm uppercase tracking-widest mb-3">
              HAKKIMIZDA
            </p>
            <h1 className="text-4xl md:text-5xl font-bold text-slate-900 tracking-tight mb-6">
              İstanbul'un Güvenilir İkinci El Eşya Alım Merkezi
            </h1>
            <p className="text-lg text-slate-600 leading-relaxed">
              Beyaz Eşya Al Sat olarak yıllardır İstanbul genelinde ikinci el beyaz eşya, televizyon ve elektronik eşya alım satımı yapıyoruz. Müşteri memnuniyeti odaklı çalışma anlayışımız ve profesyonel hizmet kalitemizle sektörde güvenilir bir isim haline geldik.
            </p>
          </div>
        </div>
      </section>

      {/* Story Section */}
      <section className="py-16 md:py-24" data-testid="about-story">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl font-bold text-slate-900 tracking-tight mb-6">
                Hikayemiz
              </h2>
              <div className="space-y-4 text-slate-600 leading-relaxed">
                <p>
                  Beyaz Eşya Al Sat, İstanbul'da ikinci el eşya piyasasındaki güven eksikliğini gidermek amacıyla kuruldu. Amacımız, kullanılmayan veya yenilemek istediğiniz eşyalarınızı en uygun fiyatla ve en kolay şekilde nakite çevirmenizi sağlamaktır.
                </p>
                <p>
                  <strong>İkinci el beyaz eşya alan yerler</strong> arasında öne çıkan firmamız, buzdolabı, çamaşır makinesi, bulaşık makinesi gibi beyaz eşyaların yanı sıra <strong>ikinci el televizyon alan yerler</strong> kategorisinde de hizmet vermektedir.
                </p>
                <p>
                  Led TV, LCD TV, Smart TV, QLED TV ve Android TV dahil tüm televizyon türlerinde <strong>ikinci el led tv alanlar</strong> olarak adil fiyat teklifi sunuyoruz. Ayrıca <strong>ikinci el elektronik eşya alanlar</strong> olarak ev elektroniği ve akıllı cihazlarınızı da değerlendiriyoruz.
                </p>
                <p>
                  Sıfır, ikinci el veya defolu fark etmeksizin tüm eşyalarınız için kapınıza kadar geliyor, yerinde ekspertiz yapıyor ve anında nakit ödeme gerçekleştiriyoruz.
                </p>
              </div>
            </div>
            <div>
              <img
                src="https://images.unsplash.com/photo-1759936802498-2b932ac6e66f?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzR8MHwxfHNlYXJjaHwxfHxtb3ZpbmclMjBkZWxpdmVyeSUyMHZhbnxlbnwwfHx8fDE3NzUxMjYxNTd8MA&ixlib=rb-4.1.0&q=85"
                alt="Eşya taşıma ve teslimat hizmeti"
                className="rounded-2xl shadow-xl w-full object-cover h-[400px]"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Values Section */}
      <section className="py-16 md:py-24 bg-slate-50" data-testid="about-values">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-slate-900 tracking-tight mb-4">
              Değerlerimiz
            </h2>
            <p className="text-slate-600 max-w-2xl mx-auto">
              Müşterilerimize en iyi hizmeti sunmak için benimsediğimiz temel değerler.
            </p>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {values.map((value, index) => (
              <div
                key={index}
                className="bg-white p-6 rounded-xl border border-slate-200 card-hover"
                data-testid={`value-card-${index}`}
              >
                <div className="w-12 h-12 rounded-lg bg-[#002FA7] text-white flex items-center justify-center mb-4">
                  <value.icon className="w-6 h-6" />
                </div>
                <h3 className="font-semibold text-lg text-slate-900 mb-2">{value.title}</h3>
                <p className="text-slate-600 text-sm">{value.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-16 md:py-24" data-testid="about-stats">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 text-center">
            {[
              { number: "10+", label: "Yıllık Tecrübe" },
              { number: "5000+", label: "Mutlu Müşteri" },
              { number: "7/24", label: "Hizmet" },
              { number: "İstanbul", label: "Geneli Kapsıyor" },
            ].map((stat, index) => (
              <div key={index} data-testid={`stat-${index}`}>
                <p className="text-4xl md:text-5xl font-bold text-[#002FA7] mb-2">{stat.number}</p>
                <p className="text-slate-600">{stat.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Us Section */}
      <section className="py-16 md:py-24 bg-slate-50" data-testid="about-why-us">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl mx-auto">
            <h2 className="text-3xl font-bold text-slate-900 tracking-tight mb-6 text-center">
              Neden Beyaz Eşya Al Sat?
            </h2>
            <ul className="space-y-4">
              {[
                "İstanbul'un her semtine ücretsiz servis",
                "Anında ve nakit ödeme garantisi",
                "Sıfır, ikinci el ve defolu eşya alımı",
                "Profesyonel ekspertiz ve adil fiyatlandırma",
                "7 gün 24 saat ulaşılabilirlik",
                "Hızlı ve güvenilir hizmet",
                "WhatsApp üzerinden kolay teklif alma",
                "Aynı gün hizmet imkanı",
              ].map((item, index) => (
                <li key={index} className="flex items-center gap-3 bg-white p-4 rounded-lg border border-slate-200">
                  <CheckCircle className="w-5 h-5 text-[#128C7E] flex-shrink-0" />
                  <span className="text-slate-700">{item}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 md:py-24 bg-[#002FA7]" data-testid="about-cta">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-white tracking-tight mb-4">
            Hemen Bizimle İletişime Geçin
          </h2>
          <p className="text-blue-100 text-lg mb-8">
            Eşyanızın fotoğrafını gönderin, anında fiyat teklifi alın.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a
              href={`tel:${phoneNumber}`}
              className="inline-flex items-center justify-center gap-2 bg-white text-[#002FA7] px-6 py-3.5 rounded-lg font-semibold hover:bg-slate-100 transition-colors"
              data-testid="about-cta-phone-btn"
            >
              <Phone className="w-5 h-5" />
              <span>0507 553 70 51</span>
            </a>
            <a
              href={whatsappUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center justify-center gap-2 bg-[#25D366] text-white px-6 py-3.5 rounded-lg font-semibold hover:bg-[#128C7E] transition-colors"
              data-testid="about-cta-whatsapp-btn"
            >
              <MessageCircle className="w-5 h-5" />
              <span>WhatsApp'tan Yaz</span>
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default AboutPage;
